/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as React from 'react';

import { IEveCatalogCheckerData } from './eve-catalog-checker.data';
import { IEveCatalogCheckerProps } from './eve-catalog-checker.props.autogenerated';
import { GetCatalogIdFromContext } from '../../utilities';
import { computed } from 'mobx';
import { ArrayExtensions } from '@msdyn365-commerce-modules/retail-actions';
import Msdyn365 from '@msdyn365-commerce/core';
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from '@msdyn365-commerce-modules/utilities';

export interface IEveCatalogCheckerViewProps extends IEveCatalogCheckerProps<IEveCatalogCheckerData> {
    modal: React.ReactNode;
}

interface IEveCatalogCheckerState {
    displayModal: boolean;
    isInitialized: boolean;
}

/**
 *
 * EveCatalogChecker component
 * @extends {React.PureComponent<IEveCatalogCheckerProps<IEveCatalogCheckerData>>}
 */
class EveCatalogChecker extends React.PureComponent<IEveCatalogCheckerProps<IEveCatalogCheckerData>, IEveCatalogCheckerState> {
    private static readonly _catalogIdQueryParamKey: string = 'catalogid';
    constructor(props: IEveCatalogCheckerProps<IEveCatalogCheckerData>) {
        super(props);
        this.state = {
            displayModal: false,
            isInitialized: false
        };
        console.log('Init');
    }
    @computed private get catalogId() {
        return GetCatalogIdFromContext(this.props.context.actionContext);
    }

    @computed private get cartCatalogId(): number {
        const {
            data: {
                cart: { result: cart }
            }
        } = this.props;

        if (cart?.cart && ArrayExtensions.hasElements(cart.cart.CartLines)) {
            return cart.cart.CartLines[0].CatalogId || 0;
        }
        return 0;
    }

    public componentDidMount() {
        if (this.catalogId > 0 && this.cartCatalogId !== 0 && this.cartCatalogId !== this.catalogId) {
            // We have to prompt user to change catalogs
            this.setState({ displayModal: true });
        }
    }

    private _doesLinkHaveCatalogIdQueryParam(link: string): boolean {
        return link.toLowerCase().indexOf(EveCatalogChecker._catalogIdQueryParamKey) > -1;
    }

    private _rebuildLinkWithCatalogIdQueryParam(link: string): string {
        let concatSymbol: '&' | '?' = '?';

        if (link.indexOf(concatSymbol) > -1) {
            concatSymbol = '&';
        }

        return `${link}${concatSymbol}${EveCatalogChecker._catalogIdQueryParamKey}=${this.catalogId}`;
    }

    private _addAnchorToTagChildren(element: Element) {
        if (element.nextElementSibling) {
            this._addAnchorToTagChildren(element.nextElementSibling);
        }
        for (let i = 0; i < element.children.length; i++) {
            // eslint-disable-next-line security/detect-object-injection
            const tag = element.children[i];
            if (tag.children.length) {
                this._addAnchorToTagChildren(tag);
            }
            if (tag.tagName.toLowerCase() === 'a') {
                const anchorTag = tag as HTMLAnchorElement;
                if (!this._doesLinkHaveCatalogIdQueryParam(anchorTag.href)) {
                    anchorTag.href = this._rebuildLinkWithCatalogIdQueryParam(anchorTag.href);
                }
            }
        }
    }

    private _isExternalUrl(url: any) {
        return new URL(url).origin !== location.origin;
    }

    private _initialize() {
        if (this.catalogId > 0) {
            // Add catalog ID to all links on the page
            const anchorTags = document.querySelectorAll<HTMLAnchorElement>('a');
            anchorTags.forEach(tag => {
                if (tag.href) {
                    if (!this._isExternalUrl(tag) && !this._doesLinkHaveCatalogIdQueryParam(tag.href)) {
                        tag.href = this._rebuildLinkWithCatalogIdQueryParam(tag.href);
                    }
                }
            });

            // Add event listener to all buttons that render a dropdown menu on click
            const buttons = document.querySelectorAll<HTMLButtonElement>('button[aria-expanded="false"]');
            buttons.forEach(btn => {
                btn.addEventListener('click', () => {
                    setTimeout(() => {
                        this._addAnchorToTagChildren(btn.parentElement!);
                    }, 50);
                });
            });
        }
    }

    private _renderCatalogPromptModal() {
        const {
            resources: { catalogPromptBodyText, catalogPromptHeading, changeCatalogButtonText }
        } = this.props;
        return (
            <Modal isOpen={this.state.displayModal}>
                <ModalHeader>
                    <span>{catalogPromptHeading}</span>
                </ModalHeader>
                <ModalBody>{catalogPromptBodyText}</ModalBody>
                <ModalFooter>
                    <Button className='change-catalog-button'>
                        <a href={this._buildUrlToNewCatalog()}>{changeCatalogButtonText}</a>
                    </Button>

                    {/*{this._renderSaveToOrderTemplateButton()}*/}
                </ModalFooter>
            </Modal>
        );
    }

    // private _renderSaveToOrderTemplateButton() {
    //     const { resources } = this.props;
    //     return (
    //         <SaveCartToOrderTemplateComponent
    //             addToOrderTemplateDialogResources={this._generateAddToOrderTemplateDialogResources(resources)}
    //             createOrderTemplateDialogResources={this._generateOrderTemplateNameDialogResources(resources)}
    //             linesAddedToOrderTemplateDialogResources={this._generateLinesAddedToOrderTemplateDialogResources(resources)}
    //             createOrderTemplateFromCartButton={resources.createOrderTemplateFromCartButton}
    //             createOrderTemplateFromCartButtonTooltip={resources.createOrderTemplateFromCartButtonTooltip}
    //             context={this.props.context}
    //             id={this.props.id}
    //             typeName={this.props.typeName}
    //             data={{
    //                 orderTemplates: this.props.data.orderTemplates.result || [],
    //                 cartLines: this.props.data.cart.result?.cart.CartLines || [],
    //                 customerInformation: this.props.data.customerInformation.result
    //             }}
    //         />
    //     );
    // }
    //
    // private _generateOrderTemplateNameDialogResources(resources: IEveCatalogCheckerResources): IOrderTemplateNameDialogResources {
    //     return {
    //         orderTemplateHeaderLabel: resources.createOrderTemplateHeader,
    //         orderTemplateTitleLabel: resources.orderTemplateTitle,
    //         orderTemplateNameAriaLabel: resources.orderTemplateNameAriaLabel,
    //         orderTemplateDescription: resources.createOrderTemplateDescription,
    //
    //         defaultOrderTemplateName: resources.defaultOrderTemplateName,
    //
    //         orderTemplateButtonLabel: resources.createOrderTemplateButtonText,
    //         orderTemplateCancelButtonLabel: resources.cancelNewOrderTemplateCreationButtonText
    //     };
    // }
    //
    // private _generateLinesAddedToOrderTemplateDialogResources(
    //     resources: IEveCatalogCheckerResources
    // ): ILinesAddedToOrderTemplateDialogResources {
    //     return {
    //         viewOrderTemplateButtonText: resources.viewOrderTemplateButtonText,
    //         continueShoppingButtonText: resources.continueShoppingButtonText,
    //         linesAddedToOrderTemplateHeaderItemsOneText: resources.linesAddedToOrderTemplateHeaderItemsOneText,
    //         linesAddedToOrderTemplateHeaderItemsFormatText: resources.linesAddedToOrderTemplateHeaderItemsFormatText,
    //         linesAddedToOrderTemplateHeaderLinesOneText: resources.linesAddedToOrderTemplateHeaderLinesOneText,
    //         linesAddedToOrderTemplateHeaderLinesFormatText: resources.linesAddedToOrderTemplateHeaderLinesFormatText,
    //         linesAddedToOrderTemplateHeaderMessageText: resources.linesAddedToOrderTemplateHeaderMessageText,
    //         linesAddedToOrderTemplateCustomPriceAlertMessageText: resources.linesAddedToOrderTemplateCustomPriceAlertMessageText
    //     };
    // }
    //
    // private _generateAddToOrderTemplateDialogResources(resources: IEveCatalogCheckerResources): IAddToOrderTemplateDialogResources {
    //     return {
    //         addToOrderTemplateHeader: resources.addToOrderTemplateHeader,
    //
    //         noOrderTemplatesMessage: resources.noOrderTemplatesMessage,
    //         noOrderTemplatesDescription: resources.noOrderTemplatesDescription,
    //
    //         createAnOrderTemplateButtonText: resources.createAnOrderTemplateButtonText,
    //         createNewOrderTemplateButtonText: resources.createNewOrderTemplateButtonText,
    //         cancelOrderTemplateCreationButtonText: resources.cancelOrderTemplateCreationButtonText,
    //
    //         selectTemplatesText: resources.selectTemplatesText,
    //         addToTemplateButtonText: resources.addToTemplateButtonText,
    //         lineItemsText: resources.lineItemsText
    //     };
    // }

    private _buildUrlToNewCatalog(): string {
        if (!Msdyn365.isBrowser) {
            return '';
        }
        const url = new URL(window.location.href);
        url.searchParams.set(EveCatalogChecker._catalogIdQueryParamKey, this.cartCatalogId.toString());
        return `${url.pathname}${url.search}`;
    }

    public render(): JSX.Element | null {
        if (!this.state.isInitialized && Msdyn365.isBrowser) {
            this.setState({ isInitialized: true });
            this._initialize();
        }
        return <>{this._renderCatalogPromptModal()}</>;
    }
}

export default EveCatalogChecker;
